import React, { useState, useRef, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import styles from './DateRangePicker.module.scss';

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

interface DateRangePickerProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (startDate: Date, endDate: Date) => void;
    anchorRef?: React.RefObject<HTMLElement>;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({ isOpen, onClose, onSelect, anchorRef }) => {
    const [value, setValue] = useState<Value>([null, null]);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleApply = () => {
        if (Array.isArray(value) && value[0] && value[1]) {
            onSelect(value[0], value[1]);
        }
    };

    const handleCancel = () => {
        setValue([null, null]);
        onClose();
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                anchorRef?.current &&
                !anchorRef.current.contains(event.target as Node)
            ) {
                handleCancel();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, anchorRef]);

    if (!isOpen) return null;

    // Calculate days between dates
    const getDaysBetween = () => {
        if (Array.isArray(value) && value[0] && value[1]) {
            const diffTime = Math.abs(value[1].getTime() - value[0].getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays;
        }
        return 0;
    };

    const hasValidRange = Array.isArray(value) && value[0] && value[1];

    return (
        <div className={styles.dropdown} ref={dropdownRef}>
            <div className={styles.header}>
                <h3>Select Date Range</h3>
                <button className={styles.closeButton} onClick={handleCancel}>×</button>
            </div>

            <div className={styles.content}>
                <Calendar
                    onChange={setValue}
                    value={value}
                    selectRange={true}
                    maxDate={new Date()}
                    className={styles.customCalendar}
                />
            </div>

            <div className={styles.footer}>
                <div className={styles.footerButtons}>
                    <button className={styles.cancelBtn} onClick={handleCancel}>
                        Cancel
                    </button>
                    <button
                        className={styles.applyBtn}
                        onClick={handleApply}
                        disabled={!hasValidRange}
                    >
                        Apply
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DateRangePicker;
